import { useState } from "react";
import { TrashIcon, PlusIcon } from "../Components/Icons";
import { validateOffer } from "../lib/offers/validateOffer.js";
import { canEdit } from "../constants.js";
import "./OffersTab.css";

export default function OffersTab({ brand, offers, onSaveOffers, role }) {
  const items = offers ?? [];
  const [isSaving, setIsSaving] = useState(false);
  const editable = canEdit(role);

  async function persist(nextOffers) {
    setIsSaving(true);
    try {
      await onSaveOffers(brand.id, nextOffers);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleCreate(fields) {
    const newOffer = {
      id: `offer_${crypto.randomUUID().slice(0, 8)}`,
      title: fields.title.trim(),
      description: fields.description.trim(),
      landingUrl: fields.landingUrl.trim(),
      active: true,
      createdAt: new Date().toISOString(),
    };
    await persist([...items, newOffer]);
  }

  async function handleUpdate(offerId, fields) {
    await persist(
      items.map((o) =>
        o.id === offerId
          ? { ...o, title: fields.title.trim(), description: fields.description.trim(), landingUrl: fields.landingUrl.trim() }
          : o
      )
    );
  }

  async function handleToggle(offer) {
    await persist(items.map((o) => (o.id === offer.id ? { ...o, active: !o.active } : o)));
  }

  async function handleDelete(offer) {
    if (!window.confirm(`Delete offer "${offer.title}"?`)) return;
    await persist(items.filter((o) => o.id !== offer.id));
  }

  return (
    <div className="offers-tab">
      {!editable ? <p className="offers-tab__role-notice">Editors and admins only.</p> : null}
      <NewOfferForm onCreate={handleCreate} disabled={isSaving || !editable} />

      {items.length === 0 ? (
        <p className="offers-tab__empty">
          No offers yet. The generate flow picks from these, so add at least one before creating
          assets.
        </p>
      ) : (
        <ul className="offers-tab__list">
          {items.map((offer) => (
            <OfferRow
              key={offer.id}
              offer={offer}
              disabled={isSaving || !editable}
              onUpdate={(fields) => handleUpdate(offer.id, fields)}
              onToggle={() => handleToggle(offer)}
              onDelete={() => handleDelete(offer)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function NewOfferForm({ onCreate, disabled }) {
  const [title, setTitle] = useState("");
  const [landingUrl, setLandingUrl] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState(null);

  async function handleSubmit(event) {
    event.preventDefault();
    const validationError = validateOffer({ title, landingUrl });
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    await onCreate({ title, landingUrl, description });
    setTitle("");
    setLandingUrl("");
    setDescription("");
  }

  return (
    <form onSubmit={handleSubmit} className="offers-tab__form">
      <h2 className="offers-tab__form-title">New offer</h2>
      <p className="offers-tab__form-desc">
        What the ads sell: promotion, product line, or evergreen pitch.
      </p>

      {error ? <p className="offers-tab__error">{error}</p> : null}

      <div className="offers-tab__form-grid">
        <label className="offers-tab__field">
          <span>Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            placeholder="e.g. Spring Sale — 20% off"
            disabled={disabled}
          />
        </label>
        <label className="offers-tab__field">
          <span>Landing URL</span>
          <input
            type="url"
            value={landingUrl}
            onChange={(e) => setLandingUrl(e.target.value)}
            placeholder="https://…"
            disabled={disabled}
          />
        </label>
      </div>
      <label className="offers-tab__field">
        <span>Description</span>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={1000}
          placeholder="One line on what makes this offer land (optional)"
          disabled={disabled}
        />
      </label>

      <button type="submit" disabled={disabled} className="btn btn--primary offers-tab__submit">
        <PlusIcon className="offers-tab__icon" />
        Add offer
      </button>
    </form>
  );
}

function OfferRow({ offer, disabled, onUpdate, onToggle, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(offer.title);
  const [landingUrl, setLandingUrl] = useState(offer.landingUrl);
  const [description, setDescription] = useState(offer.description);
  const [error, setError] = useState(null);

  async function handleSave(event) {
    event.preventDefault();
    const validationError = validateOffer({ title, landingUrl });
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    await onUpdate({ title, landingUrl, description });
    setEditing(false);
  }

  function handleCancel() {
    setTitle(offer.title);
    setLandingUrl(offer.landingUrl);
    setDescription(offer.description);
    setError(null);
    setEditing(false);
  }

  if (editing) {
    return (
      <li className="offer-row offer-row--editing">
        <form onSubmit={handleSave} className="offers-tab__form">
          {error ? <p className="offers-tab__error">{error}</p> : null}
          <div className="offers-tab__form-grid">
            <label className="offers-tab__field">
              <span>Title</span>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} disabled={disabled} />
            </label>
            <label className="offers-tab__field">
              <span>Landing URL</span>
              <input type="url" value={landingUrl} onChange={(e) => setLandingUrl(e.target.value)} disabled={disabled} />
            </label>
          </div>
          <label className="offers-tab__field">
            <span>Description</span>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1000} disabled={disabled} />
          </label>
          <div className="offer-row__edit-actions">
            <button type="submit" disabled={disabled} className="btn btn--primary">
              Save offer
            </button>
            <button type="button" onClick={handleCancel} className="offer-row__cancel">
              Cancel
            </button>
          </div>
        </form>
      </li>
    );
  }

  return (
    <li className={`offer-row${offer.active ? "" : " offer-row--paused"}`}>
      <div className="offer-row__main">
        <div className="offer-row__title-line">
          <h3 className="offer-row__title">{offer.title}</h3>
          <span className={`offer-row__badge${offer.active ? " offer-row__badge--active" : ""}`}>
            {offer.active ? "Active" : "Paused"}
          </span>
        </div>
        {offer.description ? <p className="offer-row__desc">{offer.description}</p> : null}
        <a href={offer.landingUrl} target="_blank" rel="noreferrer" className="offer-row__url">
          {offer.landingUrl}
        </a>
      </div>
      <div className="offer-row__actions">
        <button type="button" onClick={onToggle} disabled={disabled} className="offer-row__action-btn">
          {offer.active ? "Pause offer" : "Activate offer"}
        </button>
        <button type="button" onClick={() => setEditing(true)} disabled={disabled} className="offer-row__action-btn">
          Edit
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={disabled}
          aria-label={`Delete offer ${offer.title}`}
          className="offer-row__delete"
        >
          <TrashIcon className="offers-tab__icon" />
        </button>
      </div>
    </li>
  );
}